import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { Button, Panel, SectionTitle, Text } from '@/nova/components';

import { enabledProviders, providerClientId, readSsoConfig, ssoRedirectUri, type SsoProvider } from '../../config';
import { GOOGLE_POPUP_REDIRECT_URI, loadGis, requestGoogleCode } from '../../gis';
import { getSsoIdentities, linkGoogle, unlinkProvider, type SsoIdentityEntry } from '../../ssoClient';
import { buildTelegramAuthorize } from '../../telegramStart';
import { takeLinkError, writeSsoAttempt, type SsoLinkError } from '../../pkceState';
import { ROUTE } from '../../../../routes';
import { ProviderIcon } from '../ProviderIcon';

import { Detail, Divider, ErrorText, List, Name, Row, RowIcon, RowText } from './SsoConnections.styles';

const PROVIDER_NAME: Record<SsoProvider, string> = {
  google: 'Google',
  telegram: 'Telegram',
};

/**
 * "Connected accounts" — lists the SSO identities linked to the signed-in owner and lets them connect
 * or disconnect either provider (`GET`/`POST link`/`DELETE` under `/auth/sso`). Google connects via the
 * in-page GIS popup; Telegram (no popup model) connects via a full-page OIDC redirect in link mode
 * (`buildTelegramAuthorize(..., 'link')`), which `/telegram-callback` finishes with `linkTelegram` and
 * returns here. A link failure comes back as a one-shot `takeLinkError()` code shown below. Unlink is
 * always allowed (email login remains); admin-impersonation sessions get `FORBIDDEN`, surfaced as error.
 */
export const SsoConnections = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const token = useAppSelector((state) => state.authentication.access_token);
  const config = React.useMemo(readSsoConfig, []);
  const providers = React.useMemo(() => enabledProviders(config), [config]);

  const [identities, setIdentities] = React.useState<SsoIdentityEntry[]>([]);
  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [busy, setBusy] = React.useState<SsoProvider | null>(null);
  const [error, setError] = React.useState<boolean>(false);
  // A one-shot failure code left by the Telegram link round-trip (see `/telegram-callback` link mode),
  // read + cleared once on mount so it renders exactly once after the redirect back to settings.
  const [linkError, setLinkError] = React.useState<SsoLinkError | null>(null);
  React.useEffect(() => {
    setLinkError(takeLinkError());
  }, []);

  const refresh = React.useCallback(async (): Promise<void> => {
    if (!token) {
      return;
    }
    try {
      setIdentities(await getSsoIdentities(token));
      setError(false);
    } catch {
      // A GET /auth/sso failure (admin-impersonation FORBIDDEN, or a transient error) is surfaced
      // rather than left as an unhandled rejection that silently renders every provider as "Not
      // connected". Self-handled so callers can `await refresh()` without a second catch.
      setError(true);
    } finally {
      setLoaded(true);
    }
  }, [token]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  // Preload GIS on mount so the first "Connect" click keeps transient user activation. Fetching the
  // script inside the click handler burns the activation window on a cold cache and the Google popup is
  // blocked. This page can be reached (Settings) without ever mounting SocialLoginBar, so it needs its
  // own preload rather than relying on the login bar's (same fix, mirrored).
  React.useEffect(() => {
    if (providers.includes('google')) {
      void loadGis().catch(() => undefined);
    }
  }, [providers]);

  const connectGoogle = React.useCallback(async (): Promise<void> => {
    const clientId = providerClientId(config, 'google');
    if (!clientId || !token) {
      return;
    }
    setError(false);
    setBusy('google');
    try {
      const code = await requestGoogleCode(clientId);
      await linkGoogle(token, { code, redirect_uri: GOOGLE_POPUP_REDIRECT_URI });
      await refresh();
    } catch {
      setError(true);
    } finally {
      setBusy(null);
    }
  }, [config, refresh, token]);

  const connectTelegram = React.useCallback(async (): Promise<void> => {
    const clientId = providerClientId(config, 'telegram');
    const redirectUri = ssoRedirectUri(config);
    if (!clientId || !redirectUri || !token) {
      return;
    }
    setError(false);
    setLinkError(null);
    setBusy('telegram');
    try {
      // Full-page redirect in LINK mode (no logout: the owner stays authenticated so the callback's
      // linkTelegram has a valid token, restored from localStorage on the post-redirect boot). The
      // callback links and navigates back to the account settings section; a failure returns a
      // takeLinkError code shown here on the next mount.
      const { url, attempt } = await buildTelegramAuthorize(
        clientId,
        redirectUri,
        ROUTE.settingsSection('account'),
        undefined,
        'link',
      );
      writeSsoAttempt(attempt);
      window.location.assign(url);
      // Full-page navigation; nothing below runs.
    } catch {
      setError(true);
      setBusy(null);
    }
  }, [config, token]);

  const disconnect = React.useCallback(
    async (provider: SsoProvider): Promise<void> => {
      if (!token) {
        return;
      }
      setError(false);
      setBusy(provider);
      try {
        await unlinkProvider(token, provider);
        await refresh();
      } catch {
        setError(true);
      } finally {
        setBusy(null);
      }
    },
    [refresh, token],
  );

  if (providers.length === 0 || !loaded) {
    return null;
  }

  const linkedOf = (provider: SsoProvider): SsoIdentityEntry | undefined =>
    identities.find((entry) => entry.identity_provider === provider);

  return (
    <Panel>
      <SectionTitle title={t('Connected accounts')} />
      <List>
        {providers.map((provider, index) => {
          const linked = linkedOf(provider);
          // Marked private so the Lumio page snapshot skips it: a linked
          // Telegram number arrives exactly as the provider's OIDC claim
          // delivers it, with no normalization anywhere in the chain, so the
          // pattern mask cannot be relied on for this field.
          const detail = linked?.email ?? linked?.phone_number;
          return (
            <React.Fragment key={provider}>
              {index > 0 ? <Divider /> : null}
              <Row>
                <RowIcon aria-hidden="true">
                  <ProviderIcon provider={provider} />
                </RowIcon>
                <RowText>
                  <Name>{PROVIDER_NAME[provider]}</Name>
                  {linked ? (
                    <Detail data-lumio-private>{detail ?? t('Connected')}</Detail>
                  ) : (
                    <Detail>
                      <Text text="Not connected" />
                    </Detail>
                  )}
                </RowText>
                {linked ? (
                  <Button
                    label="Disconnect"
                    variant="outline"
                    disabled={busy !== null}
                    onClick={() => void disconnect(provider)}
                  />
                ) : (
                  <Button
                    label="Connect"
                    disabled={busy !== null}
                    onClick={() => void (provider === 'google' ? connectGoogle() : connectTelegram())}
                  />
                )}
              </Row>
            </React.Fragment>
          );
        })}
      </List>
      {error && (
        <ErrorText>
          <Text text="Could not update your connected accounts. Please try again." />
        </ErrorText>
      )}
      {linkError === 'already' && (
        <ErrorText role="alert">
          <Text text="This Telegram account is already linked to another account." />
        </ErrorText>
      )}
      {linkError === 'expired' && (
        <ErrorText role="alert">
          <Text text="Your session expired. Please try connecting again." />
        </ErrorText>
      )}
      {linkError === 'failed' && (
        <ErrorText role="alert">
          <Text text="Could not connect Telegram. Please try again." />
        </ErrorText>
      )}
    </Panel>
  );
};

export default SsoConnections;
